import { SiteUrls } from '@tableau/shared-urls';
import { BodyCell, DataGrid } from '@tableau/widgets-datagrid';
import * as React from 'react';

import { Messages } from '../../codegen/Localize';
import { CommonDataGridColumns } from '../../common/react/CommonDataGridColumns';
import { DataGridCells } from '../../common/react/components/dataGrid/DataGridCells';
import { SortableHeader } from '../../common/react/components/dataGrid/SortableHeader';
import { RightAlignedCell, RightAlignedHeader } from '../../common/react/components/dataGrid/StyledCellWrapper';
import { IReactTranslateService } from '../../common/react/IReactTranslateService';
import { FlowDataGridCells } from '../../flow/FlowDataGridDefinitions';
import { getFlowOutputStepRunIconClass } from '../../flow/utils/FlowIconUtils';
import { localizeFlowRunHistoryStatus } from '../../flow/utils/FlowStatusUtils';
import { IScheduleFrequencyDescriptionFilter } from '../../ts/filters/ScheduleFrequencyDescriptionFilter';
import { FlowOutputStepRunListItemInfo, FlowOutputStepRunType } from '../../ts/models/FlowOutputStepRunInfo';

export type FlowOutputStepRunGridScope = CommonDataGridColumns.ISelectColumnScope<FlowOutputStepRunListItemInfo>;

export module FlowOutputStepRunGrid {

  export function columns(
    scope: FlowOutputStepRunGridScope,
    translate: IReactTranslateService,
    scheduleFrequencyDescriptionFilter: IScheduleFrequencyDescriptionFilter,
    siteUrls: SiteUrls,
    isServerOrSiteAdmin: boolean,
    isDownloadAllowed: boolean,
  ): DataGrid.Column[] {

    const outputColumn: DataGrid.Column = {
      name: 'output-col',
      dataKey: 'flowOutputStepName',
      headerText: translate('flowRunHistoryColumn_output'),
      sortable: true,
      headerFormatter: SortableHeader.Formatter(),
      direction: 'asc',
      cellFormatter: DataGridCells.PlainTextCell('outputName')
    };

    const scheduleColumn: DataGrid.Column = {
      name: 'schedule-col',
      dataKey: 'schedule',
      headerText: translate('flowRunHistoryColumn_runTypeTitle'),
      sortable: false,
      headerFormatter: SortableHeader.Formatter(),
      direction: 'asc',
      cellFormatter: RunTypeCell()
    };

    const startColumn: DataGrid.Column = {
      name: 'start-col',
      dataKey: 'startedAt',
      headerText: translate('flowRunHistoryColumn_runStart'),
      sortable: true,
      headerFormatter: SortableHeader.Formatter(),
      direction: 'desc',
      cellFormatter: DataGridCells.DateTimeCell('startedAt')
    };

    const endColumn: DataGrid.Column = {
      name: 'end-col',
      dataKey: 'completedAt',
      headerText: translate('flowRunHistoryColumn_runEnd'),
      sortable: true,
      headerFormatter: SortableHeader.Formatter(),
      direction: 'desc',
      cellFormatter: DataGridCells.DateTimeCell('completedAt')
    };

    const durationColumn: DataGrid.Column = {
      name: 'duration-col',
      dataKey: 'duration',
      headerText: translate('flowRunHistoryColumn_duration'),
      sortable: false,
      headerFormatter: SortableHeader.Formatter(),
      cellFormatter: DataGridCells.DurationCell('startedAt', 'completedAt'),
    };

    const statusColumn: DataGrid.Column = {
      name: 'status-col',
      dataKey: 'status',
      headerText: translate('flowRunHistoryColumn_status'),
      sortable: true,
      headerFormatter: SortableHeader.Formatter(),
      cellFormatter: DataGridCells.PlainTextCellWithIcon((row: any) => {
        const rowData = row as FlowOutputStepRunListItemInfo;
        return {
          text: localizeFlowRunHistoryStatus(rowData.status),
          iconClass: getFlowOutputStepRunIconClass(rowData.status)
        };
      })
    };

    const rowsProducedColumn: DataGrid.Column = {
      name: 'rows-produced-col',
      dataKey: 'rowsProduced',
      headerText: translate('flowRunHistoryColumn_rowsGeneratedTitle'),
      sortable: true,
      direction: 'desc',
      headerFormatter: RightAlignedHeader(SortableHeader.Formatter()),
      cellFormatter: RightAlignedCell(DataGridCells.LocalizedCount('rowsProduced')),
    };

    const errorColumn: DataGrid.Column = {
      name: 'errors-col',
      dataKey: 'errors',
      headerText: translate('flowRunHistoryColumn_errors'),
      sortable: false,
      headerFormatter: SortableHeader.Formatter(),
      cellFormatter: FlowDataGridCells.FlowRunHistoryErrorsCell(siteUrls, isDownloadAllowed),
    };

    return [
      outputColumn,
      scheduleColumn,
      startColumn,
      endColumn,
      durationColumn,
      statusColumn,
      rowsProducedColumn,
      errorColumn
    ];
  }

  export function LongSelectableTextCell(propertyName: string, testId?: string): React.StatelessComponent<BodyCell.Props> {
    return function LongSelectableTextCell(props: BodyCell.Props) {
      const text = props.row.data[propertyName];
      return <div title={ text } style={{overflow: 'hidden', textOverflow: 'ellipsis'}} data-tb-test-id={ testId }>{ text }</div>;
    };
  }

  function RunTypeCell(): React.StatelessComponent<BodyCell.Props> {
    return DataGridCells.PlainTextCellWithTransform('runType',
      (runType: FlowOutputStepRunType) => {
        return runType === FlowOutputStepRunType.scheduled ? Messages.flowRunHistoryColumn_runTypeScheduled() : Messages.flowRunHistoryColumn_runTypeOndemand();
      }
    );
  }
}
